// src/client/pages/settings.tsx
import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@client/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@client/components/ui/tabs';
import { RefreshCw, Settings as LucideSettings, Database, Palette, Code, Sliders } from 'lucide-react';
import { Button } from '@client/components/ui/button';
import { ApiClient } from '@client/lib/api-client';
import { useToast } from '@client/hooks/use-toast';
import { queryKeys } from '@client/lib/query-keys';
import { useQueryClient } from '@tanstack/react-query';

// Import sub-components
import SettingsHeader from '@client/components/settings/SettingsHeader';
import ApplicationSettingsTab from '@client/components/settings/ApplicationSettingsTab';
import FeatureModulesTab from '@client/components/settings/FeatureModulesTab';
import CategoriesTab from '@client/components/settings/CategoriesTab';
import ScoreRangesTab from '@client/components/settings/ScoreRangesTab';
import SelectorsTab from '@client/components/settings/SelectorsTab';
import DataValuesTab from '@client/components/settings/DataValuesTab';
import DataManagementTab from '@client/components/settings/DataManagementTab';
import SettingsValueInput from '@client/components/settings/SettingsValueInput';
import XPathDefinitionsTab from '@client/components/settings/XPathDefinitionsTab';

// Import types
import type { 
  Setting, 
  NewSettingForm, 
  ScoreRange, 
} from '@client/types/settings';
import { 
  ProfileCategory,
  ProfileCategoryResponse,
} from '@shared/types';

import { defaultSettings } from '@shared/defaults/settings';

// ============================================================================
// Loading Skeleton
// ============================================================================

function SettingsSkeleton() {
  return (
    <div className="p-6">
      <Card>
        <CardHeader>
          <div className="h-6 w-32 bg-gray-200 rounded animate-pulse" />
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-12 bg-gray-100 rounded animate-pulse" />
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// ============================================================================
// Main Settings Component
// ============================================================================

export default function Settings() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const location = useLocation(); // ✅ Import von react-router-dom
  
  // Parse tab from URL query params - reagiert auf location changes
  const searchParams = new URLSearchParams(location.search);
  const urlTab = searchParams.get('tab') || 'app';
  
  // State
  const [activeTab, setActiveTab] = useState<string>(urlTab);
  const [searchFilter, setSearchFilter] = useState('');
  const [editingSetting, setEditingSetting] = useState<Setting | null>(null);
  const [showNewSettingDialog, setShowNewSettingDialog] = useState(false);
  const [jsonVisible, setJsonVisible] = useState<{[key: string]: boolean}>({});
  const [newSetting, setNewSetting] = useState<NewSettingForm>({
    key: '',
    value: '',
    type: 'string'
  });
  
  // Sync activeTab with URL changes
  useEffect(() => {
    const currentTab = searchParams.get('tab') || 'app';
    if (currentTab !== activeTab) {
      setActiveTab(currentTab);
    }
  }, [location.search]); // ✅ Triggert bei URL-Änderung
  
  // Sync tab changes with URL (wenn User direkt im Tab klickt)
  useEffect(() => {
    const newUrl = `/settings?tab=${activeTab}`;
    if (location.pathname + location.search !== newUrl) {
      window.history.pushState({}, '', newUrl);
    }
  }, [activeTab]);

  // ============================================================================
  // Queries
  // ============================================================================

  const { data: settings = [], isLoading: isLoadingSettings, error: settingsError } = useQuery({
    queryKey: queryKeys.settings.all(),
    queryFn: async () => {
      const data = await ApiClient.get<Setting[]>('/settings');
      return Array.isArray(data) ? data : [];
    }
  });

  const { data: categories = [], isLoading: isLoadingCategories } = useQuery({
    queryKey: ['/profile-categories'],
    queryFn: async () => {
      const response = await ApiClient.get<ProfileCategoryResponse>('/profile-categories');
      const data = response.categories; // ✅ Auspacken des verschachtelten Objekts
      return Array.isArray(data) ? data : [];
    }
  });

  const { data: scoreRanges = [], isLoading: isLoadingScoreRanges } = useQuery({
    queryKey: ['/score-ranges'],
    queryFn: async () => {
      const data = await ApiClient.get<ScoreRange[]>('/score-ranges');
      return Array.isArray(data) ? data : [];
    }
  });

  // ============================================================================
  // Mutations
  // ============================================================================

  const saveSetting = async (key: string, value: string, type?: string) => {
    try {
      const currentSetting = settings.find(s => s.key === key);
      const settingType = type || currentSetting?.type || 'string';

      // ✅ Serialisiere JSON-Objekte falls nötig
      let serializedValue = value;
      if (settingType === 'json' && typeof value === 'object') {
        serializedValue = JSON.stringify(value);
      }
      
      await ApiClient.put(`/settings/${key}`, {
        value: serializedValue,
        type: settingType
      });
      queryClient.invalidateQueries({ queryKey: queryKeys.settings.all() });
      setEditingSetting(null);
      toast({
        title: "Erfolg",
        description: "Einstellung wurde gespeichert.",
      });
    } catch (err) {
      toast({
        title: "Fehler",
        description: "Einstellung konnte nicht gespeichert werden.",
        variant: "destructive",
      });
    }
  };

  const createSetting = async (setting: NewSettingForm) => {
    try {
      await ApiClient.put(`/settings/${setting.key}`, {
        value: setting.value,
        type: setting.type || 'string'
      });
      queryClient.invalidateQueries({ queryKey: queryKeys.settings.all() });
      setShowNewSettingDialog(false);
      setNewSetting({ key: '', value: '', type: 'string' });
      toast({
        title: "Erfolg",
        description: "Einstellung wurde erstellt.",
      });
    } catch (err) {
      toast({
        title: "Fehler",
        description: "Einstellung konnte nicht erstellt werden.",
        variant: "destructive",
      });
    }
  };

  const deleteSetting = async (key: string) => {
    try {
      await ApiClient.delete(`/settings/${key}`);
      queryClient.invalidateQueries({ queryKey: queryKeys.settings.all() });
      toast({
        title: "Erfolg",
        description: "Einstellung wurde gelöscht.",
      });
    } catch (err) {
      toast({
        title: "Fehler",
        description: "Einstellung konnte nicht gelöscht werden.",
        variant: "destructive",
      });
    }
  };

  const resetToDefaults = async () => {
    try {
      const deletePromises = settings.map(setting =>
        ApiClient.delete(`/settings/${setting.key}`)
      );
      await Promise.all(deletePromises);
      
      const createPromises = defaultSettings.map(setting =>
        ApiClient.put(`/settings/${setting.key}`, {
          value: setting.value,
          type: setting.type || 'string'
        })
      );
      await Promise.all(createPromises);
      
      queryClient.invalidateQueries({ queryKey: queryKeys.settings.all() });
      toast({
        title: "Erfolg",
        description: "Einstellungen wurden zurückgesetzt.",
      });
    } catch (err) {
      toast({
        title: "Fehler",
        description: "Einstellungen konnten nicht zurückgesetzt werden.",
        variant: "destructive",
      });
    }
  };

  const resetScoreRangesToDefaults = async () => {
    try {
      await ApiClient.post('/score-ranges/reset');
      queryClient.invalidateQueries({ queryKey: ['/score-ranges'] });
      toast({
        title: "Erfolg",
        description: "Score Ranges wurden zurückgesetzt.",
      });
    } catch (err) {
      toast({
        title: "Fehler",
        description: "Score Ranges konnten nicht zurückgesetzt werden.",
        variant: "destructive",
      });
    }
  };

  // ============================================================================
  // Helper Functions
  // ============================================================================

  const loadCategories = async () => {
    queryClient.invalidateQueries({ queryKey: ['/profile-categories'] });
  };

  const loadScoreRanges = async () => {
    queryClient.invalidateQueries({ queryKey: ['/score-ranges'] });
  };

  const loadAllData = async () => {
    queryClient.invalidateQueries({ queryKey: queryKeys.settings.all() });
    queryClient.invalidateQueries({ queryKey: ['/profile-categories'] });
    queryClient.invalidateQueries({ queryKey: ['/score-ranges'] });
  };

  const getSettingValue = (key: string, defaultValue: string = '') => {
    const setting = settings.find(s => s.key === key);
    return setting?.value || defaultValue;
  };

  const updateSettingValue = (key: string, value: string) => {
    saveSetting(key, value);
  };

  const renderValueInput = (setting: Setting) => {
    return (
      <SettingsValueInput
        setting={setting}
        editingSetting={editingSetting}
        setEditingSetting={setEditingSetting}
        jsonVisible={jsonVisible}
        setJsonVisible={setJsonVisible}
        saveSetting={saveSetting}
      />
    );
  };

  // ============================================================================
  // Render States
  // ============================================================================

  if (isLoadingSettings) {
    return <SettingsSkeleton />;
  }

  if (settingsError) {
    return (
      <div className="p-6">
        <Card className="border-red-200 bg-red-50">
          <CardHeader>
            <CardTitle className="text-red-800">Fehler beim Laden</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-red-700">Einstellungen konnten nicht geladen werden.</p>
            <Button onClick={loadAllData} className="mt-4">
              <RefreshCw className="h-4 w-4 mr-2" />
              Erneut versuchen
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // ============================================================================
  // Main Render
  // ============================================================================

  return (
    <>
      {/* Header Component */}
      <SettingsHeader
        searchFilter={searchFilter}
        setSearchFilter={setSearchFilter}
        showNewSettingDialog={showNewSettingDialog}
        setShowNewSettingDialog={setShowNewSettingDialog}
        newSetting={newSetting}
        setNewSetting={setNewSetting}
        createSetting={createSetting}
        resetToDefaults={resetToDefaults}
      />

      <div className="px-6 pb-6 space-y-6">
        {/* Settings Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
          <TabsList className="grid w-full grid-cols-7">
            <TabsTrigger value="app" className="flex items-center gap-2">
              <LucideSettings className="h-4 w-3" />
              <span className="hidden sm:inline">Application</span>
            </TabsTrigger>
            <TabsTrigger value="features" className="flex items-center gap-2">
              <Sliders className="h-4 w-3" />
              <span className="hidden sm:inline">Features</span>
            </TabsTrigger>
            <TabsTrigger value="categories" className="flex items-center gap-2">
              <Palette className="h-4 w-3" />
              <span className="hidden sm:inline">Categories</span>
            </TabsTrigger>
            <TabsTrigger value="scores" className="flex items-center gap-2">
              <Database className="h-4 w-3" />
              <span className="hidden sm:inline">Scores</span>
            </TabsTrigger>
            <TabsTrigger value="xpath-definitions" className="flex items-center gap-2">
              <Code className="h-4 w-3" />
              <span className="hidden sm:inline">XPath</span>
            </TabsTrigger>
            <TabsTrigger value="data-values" className="flex items-center gap-2">
              <Database className="h-4 w-3" />
              <span className="hidden sm:inline">Data</span>
            </TabsTrigger>
            <TabsTrigger value="system" className="flex items-center gap-2">
              <Database className="h-4 w-3" />
              <span className="hidden sm:inline">System</span>
            </TabsTrigger>
          </TabsList>

          {/* Application Tab */}
          <TabsContent value="app">
            <ApplicationSettingsTab
              settings={settings}
              renderValueInput={renderValueInput}
              deleteSetting={deleteSetting}
            />
          </TabsContent>

          {/* Feature Modules Tab */}
          <TabsContent value="features">
            <FeatureModulesTab
              settings={settings}
              getSettingValue={getSettingValue}
              updateSettingValue={updateSettingValue}
            />
          </TabsContent>

          {/* Categories Tab */}
          <TabsContent value="categories">
            <CategoriesTab
              categories={categories}
              loadCategories={loadCategories}
              isLoading={isLoadingCategories}
            />
          </TabsContent>

          {/* Score Ranges Tab */}
          <TabsContent value="scores">
            <ScoreRangesTab
              scoreRanges={scoreRanges}
              loadScoreRanges={loadScoreRanges}
              resetScoreRangesToDefaults={resetScoreRangesToDefaults}
              isLoading={isLoadingScoreRanges}
            />
          </TabsContent>

          {/* Selectors Tab */}
          <TabsContent value="selectors">
            <SelectorsTab />
          </TabsContent>

          <TabsContent value="xpath-definitions">
            <XPathDefinitionsTab />
          </TabsContent>

          {/* Data Values Tab */}
          <TabsContent value="data-values">
            <DataValuesTab />
          </TabsContent>

          {/* System Management Tab */}
          <TabsContent value="system">
            <DataManagementTab loadSettings={loadAllData} />
          </TabsContent>
        </Tabs>

        {/* Statistics */}
        <Card>
          <CardHeader>
            <CardTitle>Übersicht</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="text-center">
                <div className="text-2xl font-bold">{settings.length}</div>
                <div className="text-sm text-muted-foreground">Einstellungen</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold">{categories.length}</div>
                <div className="text-sm text-muted-foreground">Kategorien</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold">{scoreRanges.length}</div>
                <div className="text-sm text-muted-foreground">Score Ranges</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold">
                  {settings.filter(s => s.type === 'boolean').length}
                </div>
                <div className="text-sm text-muted-foreground">Boolean</div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </>
  );
}